import React, { useState } from 'react';

import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Drawer from '@material-ui/core/Drawer';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Media from 'react-responsive';
import { useHistory } from 'react-router';

import { showComponentOnSelectedRoute } from 'components/app/helpers';
import { customizedBellAlert, getBellAlertIdType } from 'components/bellAlerts/helpers';
import BellAlertCard from 'components/bellAlerts/partials/BellAlertCard';
import BellAlertCountHeader from 'components/bellAlerts/partials/BellAlertCountHeader';
import EmptyBellAlertList from 'components/bellAlerts/partials/EmptyBellAlertList';
import ErrorMessage from 'components/bellAlerts/partials/ErrorMessage';
import SwitchButton from 'components/bellAlerts/partials/SwitchButton';
import { TExpandedCard } from 'components/bellAlerts/types';
import { SHOW_MAINTENANCE_NOTIF } from 'constants/constants';
import routes from 'constants/routes';
import { timeNow, toUTC } from 'helpers/day';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { device } from 'helpers/screenSizes';
import { isKycRefreshExpired } from 'helpers/utils';
import useBellAlerts from 'hooks/useBellAlerts';
import { assetSelector, userSelector } from 'selectors';
import { useAppSelector } from 'storeHooks';
import NewBatch from 'styles/assets/bellAlerts/NewBatch';
import { NotificationIcon } from 'styles/assets/icons';
import ChevronRight from 'styles/assets/icons/ChevronRight';

import styles from './bellAlerts.module.scss';

const BellAlerts = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<TExpandedCard>({
    id: null,
    isUserBellAlert: false,
  });

  const {
    isRead,
    handleRead,
    handleToggle,
    isNewBellAlerts,
    setIsNewBellAlerts,
    setLastActiveTime,
    displayBellAlerts,
    bellAlertFilters,
    responseStatus,
  } = useBellAlerts();

  // const showTicker = useAppSelector(state => state.user.preferences?.showTicker);
  const showMaintenanceBanner = useAppSelector(
    state => state.settings?.show_maintenance_banner
  );
  const history = useHistory();

  const user = useAppSelector(userSelector);

  const { is_kyc_refresh_required: isKycRefreshRequired } = user;

  const isRefreshDeadlineCrossed = isKycRefreshExpired(user);
  const showRefreshKycStrip =
    isKycRefreshRequired &&
    showComponentOnSelectedRoute(SHOW_MAINTENANCE_NOTIF)(history.location.pathname);

  const { t } = useTranslation(['common']);

  const assetsData = useAppSelector(assetSelector);
  const assetsPrecisionData = Object.values(assetsData).map(
    // eslint-disable-next-line camelcase
    ({ minimum_precision, symbol }) => ({ minimumPrecision: minimum_precision, symbol })
  );

  // const isTickerAvailable = () => {
  //   return history.location.pathname === routes.easy_options.trade
  //     ? false
  //     : isTradePage();
  // };

  const handleViewAll = () => {
    history.push(routes.bell_alerts);
    setIsDrawerOpen(false);
  };

  const bellIconClickHandler = () => {
    setIsNewBellAlerts(false);
    if (!isDrawerOpen) {
      trackMixpanelEvent('Bell Alerts Clicked');
    }
    if (history.location.pathname !== '/bell_alerts') {
      setIsDrawerOpen(!isDrawerOpen);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    if (expandedCard.id) {
      handleRead(expandedCard.id, getBellAlertIdType(expandedCard.isUserBellAlert));
    }
    setExpandedCard({ id: null, isUserBellAlert: false });
    setLastActiveTime(toUTC(timeNow()));
  };

  return (
    (<Media query={device.up.md}>
      <div className={styles.mainContainer} data-testid="bell-icon-desktop-button">
        <div className={styles.countIcon}>
          <button
            type="button"
            data-testid="bell-alert-drawer-btn"
            className={styles.drawerBtn}
            onClick={bellIconClickHandler}
          >
            <NotificationIcon className={cn({ [styles.activeBellIcon]: isDrawerOpen })} />
          </button>
          {isNewBellAlerts ? (
            <button
              type="button"
              data-testid="bell-alert-new-batch"
              onClick={bellIconClickHandler}
              className={styles.newBatch}
              data-palette="BellAlerts">
              <NewBatch />
            </button>
          ) : null}
        </div>
        <Drawer
          className={cn(styles.bellAlertDrawer, {
            [styles.heightWithBanner]:
              !showMaintenanceBanner && showRefreshKycStrip && !isRefreshDeadlineCrossed,
            // [styles.heightWithTicker]: showTicker && isTickerAvailable(),
            // [styles.heightWithBanner]: showMaintenanceBanner,
            // [styles.heightWithBoth]:
            //   isTickerAvailable() && showTicker && showMaintenanceBanner,
            // [styles.heightWithoutBoth]: !isTickerAvailable() && !showMaintenanceBanner,
          })}
          variant="temporary"
          anchor="right"
          open={isDrawerOpen}
          onClose={handleDrawerClose}
          classes={{
            root: styles.drawerRoot,
            modal: styles.drawerModal,
          }}
          elevation={0}
        >
          <ClickAwayListener onClickAway={handleDrawerClose}>
            {responseStatus === 'error' ? (
              <ErrorMessage />
            ) : (
              <div className={styles.flexCol} data-palette="BellAlerts">
                {' '}
                <BellAlertCountHeader>
                  <SwitchButton
                    buttonText={t('common:bellAlerts.showUnread')}
                    onChange={() => handleToggle('unread')}
                    checked={bellAlertFilters.unread}
                    disableRipple
                    data-testid="bell-alert-switch-btn"
                  />
                </BellAlertCountHeader>
                <div
                  className={cn(styles.bellAlertsBox, {
                    [styles.emptyList]: displayBellAlerts.length === 0,
                  })}
                >
                  {displayBellAlerts.length > 0 ? (
                    displayBellAlerts.map(bellAlert => {
                      let updatedBellAlert = bellAlert;
                      if (bellAlert?.is_user_bell_alert) {
                        updatedBellAlert = customizedBellAlert(
                          bellAlert,
                          user.is_kyc_done,
                          assetsPrecisionData
                        );
                      }
                      return (
                        <BellAlertCard
                          key={bellAlert.id}
                          details={updatedBellAlert}
                          isRead={isRead}
                          handleRead={handleRead}
                          displayIcons
                          stretch={false}
                          transparent={false}
                          expandedCard={expandedCard}
                          setExpandedCard={setExpandedCard}
                        />
                      );
                    })
                  ) : (
                    <EmptyBellAlertList />
                  )}
                </div>
                <button
                  type="button"
                  className={styles.viewAll}
                  onClick={handleViewAll}
                  data-testid="bellAlert-viewAll-button"
                >
                  {t('common:bellAlerts.viewAll')}
                  <span className={styles.chevronRight}>
                    <ChevronRight />
                  </span>
                </button>
              </div>
            )}
          </ClickAwayListener>
        </Drawer>
      </div>
    </Media>)
  );
};

export default BellAlerts;
